/**
 * The persisted recipe model (`specs/M6-A1-automation-engine.md` §4.2).
 *
 * `{version, trigger, conditions[], actions[]}`. Each node keeps monday's own `nodeId` (which is
 * also the key of its config slot for `custom` recipes) and its `workflowBlockId` as a **string**
 * — or an `ez.*` id for behaviour monday only exposes inside a `static` recipe — so the registry
 * key and the importer key are the same value.
 */
import { z } from 'zod';
import { durationSchema } from './values.js';
/** `nodeInboundFieldsSourceConfig` normalised to monday's three `sourceType` values. */
export const nodeInputSchema = z.discriminatedUnion('from', [
    /** `workflow_execution_context` — `boardId`, `userId`, … of the run. */
    z.object({ from: z.literal('context'), field: z.string().min(1) }),
    /** `automation_config` — the stored config slot of this node. */
    z.object({ from: z.literal('config'), field: z.string().min(1) }),
    /** `node_results` — an earlier node's output (`dependentNodesFieldConfig`). */
    z.object({ from: z.literal('node'), nodeId: z.string().min(1), field: z.string().min(1) }),
]);
const nodeBase = {
    nodeId: z.string().min(1),
    /** monday's `workflowBlockId` as a string, or an `ez.*` local id. */
    block: z.string().min(1),
    params: z.record(z.string(), z.unknown()).default({}),
    inputs: z.record(z.string(), nodeInputSchema).default({}),
};
export const triggerNodeSchema = z.object(nodeBase);
export const conditionNodeSchema = z.object(nodeBase);
export const actionNodeSchema = z.object({
    ...nodeBase,
    /**
     * The "…after {some time, duration}" wait of recipes 24/169. A delayed action **ends the run
     * segment**: the lock is released and the rest of the run resumes from a delayed job (R1).
     */
    delay: durationSchema.nullable().default(null),
});
export const RECIPE_VERSION = 1;
export const automationRecipeSchema = z.object({
    version: z.literal(RECIPE_VERSION).default(RECIPE_VERSION),
    trigger: triggerNodeSchema,
    conditions: z.array(conditionNodeSchema).default([]),
    /** Positional: `actions[]` runs in array order, which is monday's sentence order. */
    actions: z.array(actionNodeSchema).default([]),
});
/** Every node of a recipe, in execution order, tagged with its kind. */
export function recipeNodes(recipe) {
    return [
        { kind: 'trigger', node: recipe.trigger },
        ...recipe.conditions.map((node) => ({ kind: 'condition', node })),
        ...recipe.actions.map((node) => ({ kind: 'action', node })),
    ];
}
//# sourceMappingURL=recipe.js.map